'use client'

import { AdvertisingModalEmbedded } from '@/AdvertisingModal/AdvertisingModalEmbedded'
import { EMAIL_REGEX } from '@/validation'

const MAGIC_IN_MIND_THUMB =
  'https://vinc.gumlet.io/gallery/video-thumbnails/magic-in-mind.jpg'

const formFieldsSignup = [
  {
    name: 'name',
    label: 'Name',
    type: 'text' as const,
    placeholder: 'First and last name',
    required: true,
    validation: {
      pattern: /^[a-zA-Z]+\s+[a-zA-Z]+/,
      message: 'Please enter your first and last name',
    },
  },
  {
    name: 'email',
    label: 'Email',
    type: 'email' as const,
    placeholder: 'your@email.com',
    required: true,
    validation: {
      pattern: EMAIL_REGEX,
      message: 'Please enter a valid email address',
    },
  },
]

const shared = {
  imageUrl: MAGIC_IN_MIND_THUMB,
  imageAlt: 'Magic in Mind by Joshua Jay',
  submitButtonText: 'Send me Magic in Mind',
  successMessage:
    "You're in. Check your email for access to Magic in Mind, or visit /ebooks/magic-in-mind/.",
  formFields: formFieldsSignup,
}

const contentFullWidth = {
  ...shared,
  title: 'Magic in Mind. Free.',
  introHtml: `<p><strong>A note from Joshua Jay:</strong> What if I told you that today we are releasing a book with contributions from Derren Brown, Teller, and Juan Tamariz? How about if the book also included contributions from John Carney, Darwin Ortiz, Tommy Wonder, Pit Hartling, Rene Lavand, Tom Stone, and nearly thirty other magic luminaries?</p><p>And what if I told you that this collection was entirely free?</p><p>I spent the last three years working on <em>Magic in Mind</em>, a 500+ page gift for magicians of every level who care deeply about the art. It includes some of the most important, influential, and helpful essays on magic ever written.</p><p>Contributors include: Tommy Wonder, Charles Reynolds, Simon Aronson, Paul Harris, John Carney, Jamy Ian Swiss, Dariel Fitzke, Peter Samelson, Derren Brown, Michael Close, Pit Hartling, Eugene Burger, S.H. Sharpe, Rene Lavand, Robert-Houdin, Henning Nelms, Juan Tamariz, Rick Johnsson, Tom Stone, Darwin Ortiz, Milt Kort, Arturo de Ascanio, David Regal, Doug Conn, Ken Weber, David Kaye, Roberto Giobbi, Eberhard Riese, Brian Brushwood, Teller, Whit Haydn, Max Maven, and John Nevil Maskelyne.</p><p>If the book helps you, please share it with another magician who would benefit.</p>`,
}

const contentSidebar = {
  ...shared,
  title: 'Magic in Mind. Free.',
  introHtml: `<p>A 500+ page collection of legendary essays on magic, with contributions from Derren Brown, Teller, Juan Tamariz, Tommy Wonder, Darwin Ortiz, and many more. Enter your email to get access.</p>`,
}

const configFullWidth = {
  layout: 'side-by-side' as const,
  hideForLoggedInUsers: false,
  showFormForLoggedInUsers: true,
}

const configSidebar = {
  layout: 'stacked' as const,
  hideForLoggedInUsers: true,
  showFormForLoggedInUsers: true,
}

type MagicInMindSignupModalProps = {
  variant?: 'sidebar' | 'fullWidth'
}

export function MagicInMindSignupModal({
  variant = 'sidebar',
}: MagicInMindSignupModalProps) {
  const isSidebar = variant === 'sidebar'
  const content = isSidebar ? contentSidebar : contentFullWidth

  return (
    <section
      aria-label="Magic in Mind free ebook signup"
      className={isSidebar ? 'w-full' : undefined}
    >
      <AdvertisingModalEmbedded
        content={content}
        trackingEventName="MagicInMind signup"
        config={isSidebar ? configSidebar : configFullWidth}
        stackedContentClassName={
          isSidebar ? 'px-6 pb-8 pt-4 md:px-8 md:pb-10 md:pt-5' : undefined
        }
        formClassName={isSidebar ? 'space-y-4' : 'space-y-6'}
        className={
          isSidebar
            ? '!mb-0 w-full max-w-none !overflow-visible border-gray-200 shadow-xl'
            : '!mb-0 w-full max-w-none overflow-hidden border-gray-200 shadow-xl'
        }
        customInputClassName={
          isSidebar
            ? 'w-full rounded-xl border-2 border-gray-200 px-4 py-3 text-base text-gray-900 placeholder:text-gray-400 focus:border-amber-500 focus:outline-none focus:ring-2 focus:ring-amber-500/30'
            : 'w-full rounded-lg border border-gray-300 px-4 py-3 text-lg text-gray-900 focus:outline-none focus:ring-2 focus:ring-amber-500'
        }
        customButtonClassName={
          isSidebar
            ? 'w-full rounded-xl py-3.5 text-base font-semibold shadow-md'
            : 'w-full py-3 text-lg'
        }
        customTitleClassName={
          isSidebar
            ? 'mb-2 text-left font-archivo text-2xl font-black leading-[1.15] tracking-tight text-gray-950 sm:text-3xl'
            : 'text-left font-archivo text-2xl font-black leading-tight text-gray-900 sm:text-3xl'
        }
        customIntroClassName={
          isSidebar
            ? 'text-left text-base leading-snug text-gray-800 sm:text-lg [&_p]:mb-0'
            : 'space-y-3 text-left text-base leading-relaxed text-gray-700 [&_p]:mb-0'
        }
        customSuccessClassName={
          isSidebar ? 'space-y-3 py-10 text-center' : 'py-6 text-center'
        }
        customSuccessTitleClassName={
          isSidebar
            ? 'text-lg font-bold text-green-800'
            : 'mb-2 text-base font-semibold text-green-700'
        }
        customSuccessMessageClassName={
          isSidebar
            ? 'text-base leading-relaxed text-green-800'
            : 'text-sm text-green-700'
        }
        hideTitleInSuccess={true}
        hideLabels={false}
        hideErrorBox={false}
      />
    </section>
  )
}
